import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { coinArt, useCoinCase, useMarkViewed, isHiddenCoin, type CaseCoin } from '@/hooks/useAchievements'
import { useFocusTrap } from '@/hooks/useFocusTrap'
import { ALBUM_GROUPS, albumGroup, coinWords, coinHow, TIER } from '@/components/coins/brianCoins'
import '@/styles/coins.css'

/**
 * Achievements: the coin album (Brian's coins.html v1.4, SCREEN-INVENTORY #24).
 *
 * Wells pressed into a black page. A locked coin keeps its well and shows its
 * locked art, so the set reads as gaps rather than absence: the name reads,
 * the picture doesn't. A hidden coin is an empty well marked "?" until it is
 * earned. Ladder coins say how far along you are (3 / 10). A coin earned since
 * the album last opened drops into its well, one after another, then stays
 * put, and opening the album clears the unviewed count (badge spec §6).
 * Brian, Oct 14: launch with the individual coins; the held medals wait.
 */

const UNIT: Record<string, string> = { stories: 'stories', weeks: 'weeks', places: 'places', months: 'months' }
const fmt = (n: number) => (n >= 1000 ? n.toLocaleString('en-US') : String(n))
const day = (t: string) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

function reduced() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function CoinCase() {
  const { data, isLoading, isError } = useCoinCase()
  const markViewed = useMarkViewed()
  const [open, setOpen] = useState<CaseCoin | null>(null)
  /** Coins waiting to drop (hidden in their wells), and the one dropping now. */
  const [waiting, setWaiting] = useState<string[] | null>(null)
  const [dropping, setDropping] = useState<Set<string>>(new Set())
  const scrollRef = useRef<HTMLDivElement>(null)
  const timers = useRef<number[]>([])
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), [])

  const coins = useMemo(() => (data ?? []).filter((c) => c.is_active !== false || c.unlocked_at), [data])
  const earned = coins.filter((c) => c.unlocked_at).length

  // The drop: decided once, on the first load after opening.
  useEffect(() => {
    if (!data || waiting !== null) return
    const order = coins.filter((c) => c.unlocked_at && !c.viewed_at).map((c) => c.id)
    setWaiting(order)
    if (order.length || data.some((c) => c.unlocked_at && !c.seen_at)) markViewed.mutate()
    if (!order.length) return
    if (reduced()) { setWaiting([]); return }
    order.forEach((id, i) => {
      timers.current.push(window.setTimeout(() => {
        const el = scrollRef.current?.querySelector<HTMLElement>(`[data-coin="${id}"]`)
        el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        setWaiting((w) => (w ?? []).filter((x) => x !== id))
        setDropping((d) => new Set(d).add(id))
        timers.current.push(window.setTimeout(() => setDropping((d) => { const n = new Set(d); n.delete(id); return n }), 1500))
      }, 650 + i * 620))
    })
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) return <div className="album"><p className="album__lead">Opening the case…</p></div>
  if (isError) return <div className="album"><p className="album__lead">The case could not be opened. Try again in a moment.</p></div>

  return (
    <div className="album" ref={scrollRef}>
      <div className="album__tally"><b>{earned}</b> of {coins.length} earned</div>
      <h1 className="album__h1">Your coins</h1>
      <p className="album__lead">Earned by doing the work, never handed out. A locked coin keeps its place, so you can see what’s next.</p>

      {ALBUM_GROUPS.map((g) => {
        const set = coins.filter((c) => albumGroup(c) === g.key)
        if (!set.length) return null
        const got = set.filter((c) => c.unlocked_at).length
        return (
          <section key={g.key} className="album__sec">
            <div className="album__hd"><span className="album__n">{g.name}</span><span className="album__c"><b>{got}</b> / {set.length}</span></div>
            <div className="album__grid">
              {set.map((c) => (
                <Slot key={c.id} coin={c} wait={!!waiting?.includes(c.id)} drop={dropping.has(c.id)} onOpen={setOpen} />
              ))}
            </div>
          </section>
        )
      })}

      <p className="album__foot">Coins that pay seeds add them to your granted seeds the moment they land.</p>
      {open && <CoinSheet coin={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

function Slot({ coin, wait, drop, onOpen }: { coin: CaseCoin; wait: boolean; drop: boolean; onOpen: (c: CaseCoin) => void }) {
  const locked = !coin.unlocked_at
  const hidden = isHiddenCoin(coin)
  const p = coin.progress_current != null && coin.progress_goal != null && coin.progress_current > 0 ? coin : null
  const cls = ['coin-slot', locked && 'is-locked', hidden && 'is-hidden', wait && 'is-wait', drop && 'is-drop'].filter(Boolean).join(' ')
  return (
    <button type="button" className={cls} data-coin={coin.id} onClick={() => onOpen(coin)}
      aria-label={hidden ? 'A hidden coin' : `${coin.name}${locked ? ', locked' : ', earned'}`}>
      <span className="coin-slot__well">
        {!hidden && <img src={coinArt(coin.id, !locked, 256)} alt="" width={256} height={256} draggable={false} decoding="async" />}
      </span>
      <span className="coin-slot__nm">
        {hidden ? 'Hidden' : coin.name}
        {!hidden && locked && p
          ? <span className="coin-slot__sub">{fmt(p.progress_current!)} / {fmt(p.progress_goal!)}</span>
          : coin.tier ? <span className="coin-slot__sub">{TIER[coin.tier]}</span> : null}
      </span>
    </button>
  )
}

/** A coin, close up: earned (the date, any seeds it paid), locked (how, and
 *  how far along), or hidden (only that it's hidden). */
function CoinSheet({ coin, onClose }: { coin: CaseCoin; onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, true)
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.stopPropagation(); onClose() }
  }, [onClose])

  const locked = !coin.unlocked_at
  const hidden = isHiddenCoin(coin)
  const how = coinHow(coin)
  const at = coin.progress_current, goal = coin.progress_goal
  const hasP = locked && at != null && goal != null && goal > 0
  const pct = hasP ? Math.min(100, Math.round((100 * at!) / goal!)) : 0

  return (
    <div className="coin-scrim" role="presentation" onKeyDown={onKeyDown} onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="coin-sheet2" role="dialog" aria-modal="true" aria-label={hidden ? 'A hidden coin' : coin.name} tabIndex={-1} ref={panelRef}>
        <div className="coin-sheet2__grab" aria-hidden="true" />
        <div className="coin-sheet2__in">
          {hidden ? (
            <>
              <div className="coin-sheet2__st is-lk">Hidden</div>
              <div className="coin-sheet2__big is-hid" />
              <h3 className="is-lk">A hidden coin</h3>
              <p className="is-lk">It finds you while you’re doing something else. Nothing to chase.</p>
            </>
          ) : locked ? (
            <>
              <div className="coin-sheet2__st is-lk">Locked</div>
              <div className="coin-sheet2__big is-lk"><img src={coinArt(coin.id, false, 512)} alt="" /></div>
              <h3 className="is-lk">{coin.name}</h3>
              {coin.tier && <div className="coin-sheet2__tier">{TIER[coin.tier]}</div>}
              <p className="is-lk">{coinWords(coin)}</p>
              {how && (
                <div className="coin-sheet2__how">
                  <span className="coin-sheet2__k">How</span>{how}
                  {hasP && (
                    <>
                      <div className="coin-sheet2__bar"><i style={{ width: `${pct}%` }} /></div>
                      <span className="coin-sheet2__k coin-sheet2__k--after">{fmt(at!)} of {fmt(goal!)} {UNIT[coin.progress_unit ?? 'stories'] ?? ''}</span>
                    </>
                  )}
                </div>
              )}
              {coin.reward_seeds > 0 && <div className="coin-sheet2__seeds">Pays {coin.reward_seeds} seeds</div>}
            </>
          ) : (
            <>
              <div className="coin-sheet2__st">{coin.is_secret ? 'Found' : 'Earned'}</div>
              <div className="coin-sheet2__big"><img src={coinArt(coin.id, true, 512)} alt="" /></div>
              <h3>{coin.name}</h3>
              {coin.tier && <div className="coin-sheet2__tier">{TIER[coin.tier]}</div>}
              <p>{coinWords(coin)}</p>
              <div className="coin-sheet2__when">Earned {day(coin.unlocked_at!)}</div>
              {coin.reward_seeds > 0 && <div className="coin-sheet2__seeds">Paid {coin.reward_seeds} seeds</div>}
            </>
          )}
          <button type="button" className="coin-sheet2__close" onClick={onClose} autoFocus>Close</button>
        </div>
      </div>
    </div>
  )
}
